import React, { useState } from 'react';
import type { AlertProps, AlertVariant } from './types';

// ─── Variant class map (exported for snapshot testing) ────────────────────────

export const VARIANT_CLASSES: Record<
  AlertVariant,
  { container: string; icon: string; title: string; description: string; action: string }
> = {
  info: {
    container:   'bg-info-50 border-info-200',
    icon:        'text-info-500',
    title:       'text-info-700',
    description: 'text-info-700',
    action:      'text-info-700 hover:text-info-800',
  },
  success: {
    container:   'bg-success-50 border-success-200',
    icon:        'text-success-500',
    title:       'text-success-700',
    description: 'text-success-700',
    action:      'text-success-700 hover:text-success-800',
  },
  warning: {
    container:   'bg-warning-50 border-warning-200',
    icon:        'text-warning-500',
    title:       'text-warning-700',
    description: 'text-warning-700',
    action:      'text-warning-700 hover:text-warning-800',
  },
  danger: {
    container:   'bg-danger-50 border-danger-200',
    icon:        'text-danger-500',
    title:       'text-danger-700',
    description: 'text-danger-700',
    action:      'text-danger-700 hover:text-danger-800',
  },
};

// ─── ARIA attributes per variant ──────────────────────────────────────────────

const ARIA_ATTRS: Record<AlertVariant, { role: string; ariaLive: 'assertive' | 'polite' }> = {
  info:    { role: 'status', ariaLive: 'polite' },
  success: { role: 'status', ariaLive: 'polite' },
  warning: { role: 'alert',  ariaLive: 'polite' },
  danger:  { role: 'alert',  ariaLive: 'assertive' },
};

// ─── Default icons (internal SVGs) ───────────────────────────────────────────

function InfoIcon({ className }: { className: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-7-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM9 9a.75.75 0 0 0 0 1.5h.253l-.37 1.615A1.75 1.75 0 0 0 10.592 14H11a.75.75 0 0 0 0-1.5h-.408l.37-1.615A1.75 1.75 0 0 0 9.408 9H9Z"
      />
    </svg>
  );
}

function CheckCircleIcon({ className }: { className: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z"
      />
    </svg>
  );
}

function AlertTriangleIcon({ className }: { className: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495ZM10 5a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 10 5Zm0 9a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"
      />
    </svg>
  );
}

function XCircleIcon({ className }: { className: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM8.28 7.22a.75.75 0 0 0-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 1 0 1.06 1.06L10 11.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L11.06 10l1.72-1.72a.75.75 0 0 0-1.06-1.06L10 8.94 8.28 7.22Z"
      />
    </svg>
  );
}

const DEFAULT_ICONS: Record<AlertVariant, (className: string) => React.ReactElement> = {
  info:    (cls) => <InfoIcon className={cls} />,
  success: (cls) => <CheckCircleIcon className={cls} />,
  warning: (cls) => <AlertTriangleIcon className={cls} />,
  danger:  (cls) => <XCircleIcon className={cls} />,
};

// ─── DismissButton (internal) ─────────────────────────────────────────────────

function DismissButton({
  onDismiss,
  iconClass,
}: {
  onDismiss: () => void;
  iconClass: string;
}) {
  return (
    <button
      type="button"
      aria-label="Dismiss alert"
      onClick={onDismiss}
      className={[
        'shrink-0 rounded p-0.5 transition-opacity duration-150',
        'hover:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current focus-visible:ring-offset-1',
        iconClass,
      ].join(' ')}
    >
      <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
        <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
      </svg>
    </button>
  );
}

// ─── Alert ────────────────────────────────────────────────────────────────────

export function Alert({
  variant = 'info',
  title,
  description,
  dismissible = false,
  onDismiss,
  action,
  icon,
  className,
}: AlertProps) {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  const c = VARIANT_CLASSES[variant];
  const { role, ariaLive } = ARIA_ATTRS[variant];

  function handleDismiss() {
    setDismissed(true);
    onDismiss?.();
  }

  const resolvedIcon = icon ?? DEFAULT_ICONS[variant](`h-5 w-5 shrink-0 ${c.icon}`);

  return (
    <div
      role={role}
      aria-live={ariaLive}
      className={[
        'flex gap-3 rounded-md border p-4',
        c.container,
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {/* Icon */}
      <span className="mt-0.5 flex shrink-0">{resolvedIcon}</span>

      {/* Body */}
      <div className="flex-1 min-w-0">
        {title && (
          <p className={['text-sm font-semibold', c.title].join(' ')}>{title}</p>
        )}
        {description && (
          <div
            className={[
              'text-sm',
              c.description,
              title ? 'mt-1' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {description}
          </div>
        )}
        {action && (
          <button
            type="button"
            onClick={action.onClick}
            className={[
              'mt-2 text-sm font-semibold underline-offset-2 hover:underline',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current focus-visible:ring-offset-1 rounded',
              c.action,
            ].join(' ')}
          >
            {action.label}
          </button>
        )}
      </div>

      {/* Dismiss */}
      {dismissible && (
        <DismissButton onDismiss={handleDismiss} iconClass={c.icon} />
      )}
    </div>
  );
}
